import {
  addProperty,
  deleteFiles,
  deleteImages,
  GetpropertyByID,
  updateProperties
} from 'Api/PropertyApi';
import axios from 'axios';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Propertyschema } from 'utils/validationSchemas';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'leaflet/dist/leaflet.css';
import LocationSearch from './LocationSearch';
import { IoAddCircle } from 'react-icons/io5';
import './style.css';
interface Property {
  name: string;
  location: string;
  latitude: string;
  longitude: string;
  size?: string;
  description?: string;
  price?: string;
  images?: File[];
  photos?: { url: string }[];
  pdfs?: { url: string }[];
  files?: { url: string }[];
}

function AgentCreateProperty() {
  const location = useLocation();
  const propertyId = location.state?.property.id;
  const [imagesPreview, setImagesPreview] = useState<string[]>([]);
  const fetchingRoomDataRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [PropertyData, SetPropertyData] = useState<Property | null>(null);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const [pdfPreview, setPdfPreview] = useState<string[]>([]);

  const navigate = useNavigate();
  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
    existingFiles: File[]
  ) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files) as File[];
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      toast.error('Please choose a valid image file (JPG, PNG, etc.).');
      return;
    }

    const newFiles = files.filter(
      file =>
        !existingFiles.some(
          existingFile =>
            existingFile.name === file.name && existingFile.size === file.size
        )
    );

    if (newFiles.length === 0) {
      toast.error('Duplicate images are not allowed.');
      return;
    }

    setImagesPreview(prev => [
      ...prev,
      ...newFiles.map(file => URL.createObjectURL(file))
    ]);

    setFieldValue('images', [...existingFiles, ...newFiles]);
    if (e.target) e.target.value = '';
  };

  const handleRemoveImage = async (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    images: File[],
    imageUrls: string[],
    propertyId?: string
  ) => {
    if (images.length === 0 && imageUrls.length === 0) {
      console.log('⚠️ No images to remove. Skipping function.');
      return;
    }

    const updatedImages = images.filter((_, i) => i !== index);
    const updatedImageUrls = imageUrls.filter((_, i) => i !== index);
    setImagesPreview(prev => prev.filter((_, i) => i !== index));
    setFieldValue('images', updatedImages);
    setFieldValue('imageUrls', updatedImageUrls);
    if (propertyId && imageUrls.length > 0) {
      const imageToDelete = imageUrls[index];
      if (imageToDelete) {
        try {
          const deleteResponse = await deleteImages(
            [imageToDelete],
            propertyId
          );
          if (deleteResponse.status !== 200) {
            throw new Error('Failed to delete previous images');
          }
          console.log('✅ Image deleted successfully from server');
        } catch (error) {
          console.error('❌ Error deleting image:', error);
        }
      } else {
        console.log('⚠️ No valid image found to delete.');
      }
    } else {
      console.log('⚠️ No image deletion needed.');
    }
    if (updatedImages.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handelBackProperty = () => {
    navigate('/dashboard/properties');
  };

  const FetchPropertiesData = async () => {
    if (fetchingRoomDataRef.current) return;
    fetchingRoomDataRef.current = true;
    setIsLoading(true);
    try {
      if (!propertyId) {
        console.error('propertyId is missing.');
        return;
      }
      const response = await GetpropertyByID(propertyId);
      if (response.status === 200) {
        SetPropertyData(response.data);
      } else {
        console.error('Error fetching properties. Status:', response.status);
        toast.error('Failed to load properties.');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error fetching properties.');
    } finally {
      fetchingRoomDataRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      FetchPropertiesData();
    }
  }, [propertyId]);

  useEffect(() => {
    if (PropertyData?.photos && Array.isArray(PropertyData.photos)) {
      const existingImageUrls = PropertyData.photos
        .map(photo => (typeof photo.url === 'string' ? photo.url : ''))
        .filter(url => url);
      setImagesPreview(existingImageUrls);
    }

    if (PropertyData?.files && Array.isArray(PropertyData.files)) {
      const existingPdfUrls = PropertyData.files
        .map(file => (typeof file.url === 'string' ? file.url : ''))
        .filter(url => url);

      setPdfPreview(existingPdfUrls);
    }
  }, [PropertyData]);

  const handlePdfChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
    existingFiles: File[]
  ) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files) as File[];
    const invalidFiles = files.filter(file => file.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      toast.error('Only PDF files are allowed.');
      return;
    }

    // Avoid duplicate PDFs
    const newFiles = files.filter(
      file =>
        !existingFiles.some(existingFile => existingFile.name === file.name)
    );

    if (newFiles.length === 0) {
      toast.error('Duplicate PDFs are not allowed.');
      return;
    }
    setPdfFiles(prev => [...prev, ...newFiles]);
    setFieldValue('pdfFiles', [...existingFiles, ...newFiles]);
  };

  const handleRemovePdf = async (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    existingFiles: File[],
    fileUrls: string[],
    propertyId?: string
  ) => {
    if (existingFiles.length === 0 && fileUrls.length === 0) {
      console.log('⚠️ No PDFs to remove. Skipping function.');
      return;
    }

    const updatedFiles = existingFiles.filter((_, i) => i !== index);
    const updatedFileUrls = fileUrls.filter((_, i) => i !== index);

    setPdfFiles(updatedFiles);
    setFieldValue('pdfFiles', updatedFiles);
    setFieldValue('fileUrls', updatedFileUrls);
    if (propertyId && fileUrls.length > 0) {
      const fileToDelete = fileUrls[index];
      if (fileToDelete) {
        try {
          const deleteResponse = await deleteFiles([fileToDelete], propertyId);
          if (deleteResponse.status === 200) {
            FetchPropertiesData();
          }
        } catch (error) {
          console.error('❌ Error deleting PDF:', error);
        }
      } else {
        console.log('⚠️ No valid PDF found to delete.');
      }
    } else {
      console.log('⚠️ No PDF deletion needed.');
    }

    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="d-flex justify-content-center mt-5">
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center "
          style={{ height: '70vh', width: '60vw' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <Card
          className="p-5 shadow-sm rounded w-100"
          style={{ maxWidth: '1200px' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>{propertyId ? 'Edit property' : 'Create property'}</h2>
            <Button
              className="btn btn-primary px-5 w-md-auto"
              style={{ marginRight: '18px' }}
              onClick={handelBackProperty}
            >
              Back
            </Button>
          </div>

          <Formik
            enableReinitialize
            initialValues={{
              propertyName: PropertyData?.name || '',
              location: PropertyData?.location || '',
              latitude: PropertyData?.latitude ?? '',
              longitude: PropertyData?.longitude || '',
              size: PropertyData?.size || '',
              price: PropertyData?.price || '',
              description: PropertyData?.description || '',
              images: [] as File[],
              imageUrls: PropertyData?.photos?.map(photo => photo.url) || [],
              pdfFiles: [] as File[],
              pdfUrls: PropertyData?.files?.map(file => file.url) || []
            }}
            validationSchema={Propertyschema}
            onSubmit={async (values, { resetForm }) => {
              try {
                const formDataToSend = new FormData();
                formDataToSend.append('name', values.propertyName || '');
                formDataToSend.append('location', values.location || '');
                formDataToSend.append('description', values.description || '');
                formDataToSend.append('latitude', values.latitude || '');
                formDataToSend.append('longitude', values.longitude || '');
                formDataToSend.append('size', values.size || '');
                formDataToSend.append('price', values.price || '');

                if (Array.isArray(values.images) && values.images.length > 0) {
                  values.images.forEach(file =>
                    formDataToSend.append('images', file)
                  );
                }
                if (
                  Array.isArray(values.pdfFiles) &&
                  values.pdfFiles.length > 0
                ) {
                  values.pdfFiles.forEach(file =>
                    formDataToSend.append('files', file)
                  );
                }

                let response;
                if (propertyId) {
                  response = await updateProperties(propertyId, formDataToSend);
                } else {
                  response = await addProperty(formDataToSend);
                }

                if (response.status === (propertyId ? 200 : 201)) {
                  toast.success(
                    `Property ${
                      propertyId ? 'updated' : 'created'
                    } successfully!`
                  );
                  resetForm();
                  setImagesPreview([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  navigate('/dashboard/properties');
                } else {
                  throw new Error(
                    response.data?.message ||
                      `Failed to ${propertyId ? 'update' : 'add'} property.`
                  );
                }
              } catch (error) {
                if (axios.isAxiosError(error) && error.response) {
                  toast.error(
                    error.response.data?.message ||
                      `Failed to ${propertyId ? 'update' : 'add'} property.`
                  );
                } else {
                  toast.error(
                    (error as Error).message ||
                      `Failed to ${propertyId ? 'update' : 'add'} property.`
                  );
                }
              }
            }}
          >
            {({ setFieldValue, values, resetForm }) => {
              return (
                <FormikForm>
                  <Row>
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start">Property Name</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="propertyName"
                          placeholder="Enter property name"
                          value={values.propertyName}
                        />
                        <ErrorMessage
                          name="propertyName"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start"> Location</label>
                        <LocationSearch
                          name="location"
                          location={values.location}
                          setFieldValue={setFieldValue}
                          latitude={Number(values.latitude)}
                          longitude={Number(values.longitude)}
                        />

                        <ErrorMessage
                          name="location"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start">Area [Sq Ft]</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="size"
                          placeholder="Enter size"
                        />
                        <ErrorMessage
                          name="size"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start">Price</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="price"
                          placeholder="Enter price"
                        />
                        <ErrorMessage
                          name="price"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start">
                          Upload broschure
                        </label>

                        {/* Hidden File Input */}
                        <input
                          ref={pdfInputRef}
                          className="d-none"
                          type="file"
                          multiple
                          accept="application/pdf"
                          onChange={e =>
                            handlePdfChange(e, setFieldValue, values.pdfFiles)
                          }
                        />

                        {values.pdfFiles.length === 0 &&
                        values.pdfUrls.length === 0 ? (
                          <input
                            className="form-control"
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={e =>
                              handlePdfChange(e, setFieldValue, values.pdfFiles)
                            }
                          />
                        ) : (
                          // <Button
                          //   variant="outline-primary"
                          //   className="ms-3"
                          //   size="sm"
                          //   onClick={() => pdfInputRef.current?.click()}
                          // >
                          //   Add More
                          // </Button>
                          <input
                            className="form-control"
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={e =>
                              handlePdfChange(e, setFieldValue, values.pdfFiles)
                            }
                          />
                        )}

                        {values.pdfFiles.length > 0 && (
                          <ul
                            className="mt-2 text-muted small list-unstyled mt-4"
                            style={{ padding: '0', maxWidth: '130px' }}
                          >
                            {values.pdfFiles.map((file, index) => {
                              const fileUrl = URL.createObjectURL(file); // Create file URL

                              return (
                                <li
                                  key={index}
                                  className="d-flex justify-content-between align-items-center file-item position-relative"
                                >
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {file.name}
                                  </a>

                                  <div
                                    className="remove-btn-data -mt-5"
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleRemovePdf(
                                        index,
                                        setFieldValue,
                                        values.pdfFiles,
                                        values.pdfUrls,
                                        propertyId
                                      );
                                    }}
                                  >
                                    ❌
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <ErrorMessage
                          name="pdfFiles"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>

                      {pdfPreview.length > 0 && (
                        <Row
                          className={`g-3 ${
                            pdfPreview.length > 0 ? 'mb-3' : ''
                          }`}
                        >
                          {pdfPreview.map((url, index) => {
                            const fileName = url.split('/').pop();
                            return (
                              <Col key={index} xs={12} sm={6} md={4} lg={6}>
                                <div className="pdf-item p-2  rounded d-flex flex-column flex-sm-row align-items-center justify-content-between text-center text-sm-start position-relative">
                                  <div className="text-muted small text-truncate w-100">
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {fileName}
                                    </a>
                                  </div>

                                  <Button
                                    className="remove-btn "
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleRemovePdf(
                                        index,
                                        setFieldValue,
                                        values.pdfFiles,
                                        values.pdfUrls,
                                        propertyId
                                      );
                                    }}
                                  >
                                    ❌
                                  </Button>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      )}

                      {pdfPreview.length === 0 && pdfFiles.length === 0 && (
                        <p className="text-muted mt-2"></p>
                      )}
                    </Col>
                    <Form.Group className="mb-3">
                      <label className="fs-9 text-start">Photos</label>

                      <input
                        ref={fileInputRef}
                        className="d-none"
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={e =>
                          handleImageChange(e, setFieldValue, values.images)
                        }
                      />

                      {values.images.length === 0 &&
                      values.imageUrls.length === 0 ? (
                        <input
                          className="form-control"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={e =>
                            handleImageChange(e, setFieldValue, values.images)
                          }
                        />
                      ) : (
                        <Button
                          variant="outline-primary"
                          className="ms-3"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Add More
                        </Button>
                      )}

                      <ErrorMessage
                        name="images"
                        component="div"
                        className="text-danger fs-9 text-start mt-2"
                      />
                    </Form.Group>

                    {imagesPreview.length > 0 && (
                      <Row className="mt-3">
                        {imagesPreview.map((img, index) => (
                          <Col
                            key={index}
                            xs={6}
                            md={3}
                            className="mb-2 position-relative"
                            style={{ overflow: 'hidden' }}
                          >
                            <div className="image-container">
                              <img
                                src={img}
                                alt={`Upload ${index + 1}`}
                                className="img-fluid rounded shadow-sm"
                                style={{
                                  maxHeight: '100px',
                                  objectFit: 'cover',
                                  width: '100%'
                                }}
                              />
                              <button
                                className="delete-btn"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRemoveImage(
                                    index,
                                    setFieldValue,
                                    values.images,
                                    values.imageUrls,
                                    propertyId
                                  );
                                }}
                              >
                                ❌
                              </button>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <label className="fs-9 text-start">Description</label>
                        <div style={{ minHeight: '100px' }}>
                          <ReactQuill
                            value={values.description}
                            onChange={content =>
                              setFieldValue('description', content)
                            }
                            className="quill-editor"
                          />
                        </div>
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-danger fs-9 text-start mt-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <div className="d-flex justify-content-center gap-2 ">
                        <Button variant="primary" size="sm" type="submit">
                          {propertyId ? 'Update' : 'Submit'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            if (propertyId) {
                              navigate('/dashboard/properties');
                            } else {
                              resetForm();
                              setImagesPreview([]);
                              if (fileInputRef.current)
                                fileInputRef.current.value = '';
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </FormikForm>
              );
            }}
          </Formik>
        </Card>
      )}
    </div>
  );
}

export default AgentCreateProperty;
